<script setup lang="ts">
import axios from 'axios'
import { useVModel } from '@vueuse/core'
import { Plus } from 'lucide-vue-next'

import RewardFormBlock from '@/views/reward/components/RewardFormBlock.vue'

import LegacyRewardModal from '@/views/reward/components/RewardModal.vue'
import NewRewardModal from '@/components/rewards/RewardModal.vue'

import {
  getValidityInDaysDisclaimer,
  normalizeMerchantRewardSavePayload
} from '@/components/rewards/utils/rewardHelpers'

import type { CampaignType, LoyaltyReward, QrCodeProvider } from '@/types/types'
import type { Store } from '@/views/contests/Components/types/global'

const { t, d } = useI18n()
const { user } = useLocalStorage()
const { fetchAllRewards } = useReward()

const { flags } = useFeatureFlag()

const RewardModal = computed(() => {
  return flags.value['offer-engine']?.enabled ? NewRewardModal : LegacyRewardModal
})

const props = defineProps<{
  conversionDelay: number
  modelValue: {
    selectedReward?: number | null
    messageContent?: string
  }
  stores: Store[]
  campaignType: CampaignType
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: { selectedReward?: number | null; messageContent?: string }): void
  (e: 'handleQrCodeLink'): void
  (e: 'update:conversionDelay', value: number): void
  (e: 'reward-upserted', value: LoyaltyReward): void
}>()

const conversionDelay = useVModel(props, 'conversionDelay', emit)

const isConversionDelayTouched = ref<boolean>(false)

const selectedReward = ref<LoyaltyReward | null>(null)
const rewards = ref<LoyaltyReward[]>([])
const isRewardModalOpen = ref<boolean>(false)
const isCreateEditRewardModalOpen = ref<boolean>(false)

const rewardModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const { isQrCodeUnique, updateQrCode }: QrCodeProvider = inject('qrCode')

const handleSelectReward = (rewardId: number) => {
  const reward = rewards.value.find((r) => r.id === rewardId)
  if (reward) {
    selectedReward.value = reward

    const currentMessage = rewardModel.value.messageContent || ''
    let updatedMessage = currentMessage

    if (!updatedMessage.includes('{{ rewardLink }}')) {
      updatedMessage += updatedMessage ? ' {{ rewardLink }}' : '{{ rewardLink }}'
    }

    rewardModel.value = {
      ...rewardModel.value,
      selectedReward: rewardId,
      messageContent: updatedMessage
    }
  }
  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = false
}

const handleQrCode = () => {
  updateQrCode()
  emit('handleQrCodeLink')
}

const handleCreateReward = () => {
  rewardModel.value = {
    ...rewardModel.value,
    selectedReward: null
  }
  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = true
}

const handleEditReward = (rewardId: number) => {
  const reward = rewards.value.find((r) => r.id === rewardId)
  if (reward) {
    selectedReward.value = reward
  }
  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = true
}

const handleChangeReward = () => {
  isRewardModalOpen.value = true
}

const handleClearReward = () => {
  selectedReward.value = null
  clearSelectedRewardAndCleanMessage()
}

const fetchRewards = async () => {
  const rewardsData = await fetchAllRewards()
  rewards.value = rewardsData
}

const buildPayload = (reward: LoyaltyReward) => {
  if (!reward) return null as any

  const payload: any = {
    id: reward.id || null,
    rewardTypeId: reward.rewardType?.id || 1,
    name: reward.name,
    description: reward.description?.trim() || null,
    value: reward.value,
    externalId: reward.externalId,
    items: reward.items,
    source: reward.source || 'campaign',

    voucherDiscountAmount: reward.voucherDiscountAmount || null,
    discountValuePercent: reward.discountValuePercent || null,
    minimumOrderAmount: reward.minimumOrderAmount || null
  }

  if (reward.minimumOrderAmount !== undefined) {
    payload.minimumOrderAmount = reward.minimumOrderAmount
  }

  if (reward.isForSingleUse !== undefined) {
    payload.isForSingleUse = reward.isForSingleUse
  }

  if (reward.pictureUrl !== null) {
    payload.pictureUrl = reward.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (flags.value['offer-engine']?.enabled) {
    normalizeMerchantRewardSavePayload(payload, reward)
  }

  return payload
}

const handleSaveReward = async (reward: LoyaltyReward) => {
  try {
    const payload = buildPayload(reward)
    if (!payload) return

    let saved: any
    if (payload.id) {
      const { data } = await axios.put(`/merchants/${user.value.merchant.id}/rewards/${payload.id}`, payload)
      saved = data.data
    } else {
      const { data } = await axios.post(`/merchants/${user.value.merchant.id}/rewards`, payload)
      saved = data.data
    }

    await fetchRewards()
    const savedRewardFromList = rewards.value.find((rewardItem) => rewardItem.id === saved.id)
    if (savedRewardFromList) {
      emit('reward-upserted', savedRewardFromList)
    } else {
      emit('reward-upserted', saved)
    }
    handleSelectReward(saved.id)

    isCreateEditRewardModalOpen.value = false
  } catch (error) {
    console.error('Error saving reward:', error)
  }
}

const clearSelectedRewardAndCleanMessage = () => {
  const cleaned = (rewardModel.value.messageContent || '')
    .replaceAll('{{ rewardLink }}', '')
    .replaceAll('{{ reward }}', '')
    .replaceAll('{{ voucher }}', '')
    .replaceAll(/  +/g, ' ')
    .trim()

  rewardModel.value = {
    ...rewardModel.value,
    selectedReward: null,
    messageContent: cleaned
  }
}

const conversionDelayError = computed<string[] | []>(() =>
  conversionDelay.value < 3 || conversionDelay.value > 30
    ? [t('pages.campaign.campaignCreatePage.notifications.create.conversionDelayError')]
    : []
)

const validityInDaysDisclaimer = computed(() => {
  return getValidityInDaysDisclaimer(conversionDelay.value, d, t)
})

watch(
  () => props.modelValue.selectedReward,
  (newRewardId) => {
    if (newRewardId) {
      const reward = rewards.value.find((reward) => reward.id === newRewardId)
      if (reward) {
        selectedReward.value = reward
      } else {
        fetchRewards().then(() => {
          selectedReward.value = rewards.value.find((reward) => reward.id === newRewardId) || null
        })
      }
    } else {
      selectedReward.value = null
    }
  },
  { immediate: true }
)

watch(
  () => rewards.value,
  (newRewards) => {
    if (props.modelValue.selectedReward) {
      const reward = newRewards.find((r) => r.id === props.modelValue.selectedReward)
      if (reward && !selectedReward.value) {
        selectedReward.value = reward
      }
      if (!reward) {
        selectedReward.value = null
        clearSelectedRewardAndCleanMessage()
      }
    }
  },
  { deep: true }
)

const initializeData = async () => {
  await fetchRewards()
}

initializeData()
</script>

<template>
  <div class="rounded-md border bg-white text-sm">
    <div class="border-b px-4 py-5 sm:px-6">
      <h2 class="text-base font-medium text-foreground">
        {{ t('pages.campaign.campaignCreatePage.block.messageOptions.title') }}
      </h2>
    </div>

    <div class="space-y-6 p-6">
      <div>
        <div class="mb-4">
          <h3 class="text-sm font-medium text-foreground">
            {{ t('pages.campaign.campaignCreatePage.block.rewardBlock.title') }}
          </h3>
          <p class="mt-1 text-foreground-secondary">
            {{ t('pages.campaign.campaignCreatePage.block.rewardBlock.description') }}
          </p>
        </div>

        <div class="flex flex-col items-center justify-center rounded-md border bg-white p-7">
          <Button v-if="!selectedReward" variant="outline" class="mx-7" @click="isRewardModalOpen = true">
            <Plus />
            {{ t('pages.campaign.campaignCreatePage.block.rewardBlock.addReward') }}
          </Button>

          <RewardItem
            v-if="selectedReward"
            :reward="selectedReward"
            :is-reward-selected="true"
            :button-shown="['edit', 'change', 'clear']"
            @change-reward="handleChangeReward"
            @edit-reward="handleEditReward"
            @clear-reward="handleClearReward"
          />
        </div>

        <BaseTip
          v-if="selectedReward"
          severity="info"
          class="mt-4"
          :text="t('pages.campaign.campaignCreatePage.block.rewardBlock.info')"
        />
      </div>
      <div>
        <div class="flex flex-col justify-between sm:flex-row sm:items-center">
          <div class="mb-4 sm:mb-0">
            <h3 class="text-sm font-medium text-foreground">
              {{ t('pages.campaign.campaignCreatePage.block.qrCodeBlock.title') }}
            </h3>
            <p class="mt-1 text-foreground-secondary">
              {{ t('pages.campaign.campaignCreatePage.block.qrCodeBlock.description') }}
            </p>
          </div>
          <BaseSwitch
            id="uniqueQrCode"
            :model-value="isQrCodeUnique"
            :label="
              isQrCodeUnique
                ? t('pages.campaign.campaignCreatePage.block.qrCodeBlock.switchActivated')
                : t('pages.campaign.campaignCreatePage.block.qrCodeBlock.switchDesactivated')
            "
            font-color-gradient="600"
            :font-color="isQrCodeUnique ? 'green' : 'red'"
            is-reversed
            @click="handleQrCode"
          />
        </div>
      </div>
    </div>
  </div>

  <RewardSelectionModal
    :is-open="isRewardModalOpen"
    :rewards="rewards"
    show-edit-button
    @close-modal="isRewardModalOpen = false"
    @create-reward="handleCreateReward"
    @select-reward="handleSelectReward"
  />

  <RewardModal
    v-if="isCreateEditRewardModalOpen"
    :reward-id="selectedReward?.id"
    :usage="campaignType === 'sms' ? 'campaign_sms' : 'campaign_push'"
    :is-external-form-modified="isConversionDelayTouched"
    :has-errors="conversionDelayError.length > 0"
    hide-claim-limit-block
    @close="() => (isCreateEditRewardModalOpen = false)"
    @update:reward="handleSaveReward"
  >
    <template #additional-form-blocks>
      <RewardFormBlock :title="t('pages.reward.detail.campaignOptions')">
        <div class="flex flex-col">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.campaign.campaignCreatePage.block.conversion.validityInDays') }}</span>
          </label>
          <AppFormInput
            id="conversionDelay"
            v-model="conversionDelay"
            type="number"
            step="1"
            :min="3"
            :max="30"
            title="conversionDelay"
            :errors="conversionDelayError"
            @update:model-value="isConversionDelayTouched = true"
          />

          <p class="text-xs text-muted-foreground mt-1">{{ validityInDaysDisclaimer }}</p>
        </div>

        <BaseTip
          severity="info"
          class="mt-4"
          :text="t('pages.campaign.campaignCreatePage.block.conversion.infoRewardValidity')"
        />
      </RewardFormBlock>
    </template>
  </RewardModal>
</template>
